<template lang="html">
  <div v-if="hasContent" class="promotion-status-list flex flex-col gap-4">
    <div
      v-for="(promotion, index) in applied"
      :key="`applied-${promotion.promotionId ?? index}`"
      :class="appliedCardClass(promotion)"
    >
      <div class="flex items-start justify-between gap-2">
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2">
            <UIcon
              :name="appliedIcon(promotion)"
              :class="appliedIconClass(promotion)"
            />
            <span class="font-medium">
              {{ promotion.publicTitle || 'Акция' }}
            </span>
          </div>
          <div
            v-if="promotion.discountAmount"
            class="mt-1 text-sm text-success"
          >
            −{{ $f.currency(promotion.discountAmount) }}
          </div>
          <div
            v-if="promotion.cashbackAmount"
            class="mt-1 text-sm text-info"
          >
            +{{ $f.currency(promotion.cashbackAmount) }} кэшбэк
          </div>
        </div>
      </div>

      <PromoTermsAccordion
        v-if="getTerms(promotion)"
        :terms="getTerms(promotion)"
        class="mt-2"
      />
    </div>

    <div
      v-for="(promotion, index) in rejected"
      :key="`rejected-${promotion.promotionId ?? index}`"
      class="rounded border border-warning/40 bg-warning/5 p-3"
    >
      <div class="flex items-start gap-2">
        <UIcon
          name="i-lucide-circle-alert"
          class="mt-0.5 size-4 shrink-0 text-warning"
        />
        <div class="min-w-0 flex-1">
          <div class="font-medium">
            {{ promotion.publicTitle || 'Акция не применена' }}
          </div>
        </div>
      </div>

      <PromoTermsAccordion
        v-if="getTerms(promotion)"
        :terms="getTerms(promotion)"
        class="mt-2"
      />
    </div>

    <UAlert
      v-if="showPromoRejectedHint"
      color="warning"
      variant="soft"
      icon="i-lucide-ticket"
      title="Промокод не сработал"
      description="Проверьте условия акции ниже или измените состав заказа."
    />
  </div>
</template>

<script setup lang="ts">
import type {
  CartAppliedPromotionDto,
  CartRejectedPromotionDto,
  OrderPromotionDto,
} from '@repo/api/web/models'

import PromoTermsAccordion from './PromoTermsAccordion.vue'
import { isCashbackRewardType } from '../utils/isCashbackRewardType'

type AppliedPromotion = CartAppliedPromotionDto | OrderPromotionDto
type RejectedPromotion = CartRejectedPromotionDto

const props = withDefaults(
  defineProps<{
    applied?: AppliedPromotion[]
    rejected?: RejectedPromotion[]
    activePromoCode?: string | null
  }>(),
  {
    applied: () => [],
    rejected: () => [],
  },
)

const { $f } = useFormatter()

const hasContent = computed(
  () =>
    props.applied.length > 0
    || props.rejected.length > 0
    || showPromoRejectedHint.value,
)

const showPromoRejectedHint = computed(
  () =>
    Boolean(props.activePromoCode)
    && !props.applied.length
    && props.rejected.length > 0,
)

function getRewardType(promotion: AppliedPromotion) {
  return 'rewardType' in promotion
    ? promotion.rewardType
    : promotion.rewardTypeSnapshot
}

function appliedCardClass(promotion: AppliedPromotion) {
  const cashbackOnly = isCashbackRewardType(getRewardType(promotion))
    && !promotion.discountAmount

  return cashbackOnly
    ? 'rounded border border-info/30 bg-info/5 p-3'
    : 'rounded border border-success/30 bg-success/5 p-3'
}

function appliedIcon(promotion: AppliedPromotion) {
  return isCashbackRewardType(getRewardType(promotion))
    ? 'i-lucide-coins'
    : 'i-lucide-badge-percent'
}

function appliedIconClass(promotion: AppliedPromotion) {
  return [
    'size-4 shrink-0',
    isCashbackRewardType(getRewardType(promotion)) ? 'text-info' : 'text-success',
  ]
}

function getTerms(
  promotion: AppliedPromotion | RejectedPromotion,
): string | null {
  if ('terms' in promotion && promotion.terms) {
    return promotion.terms
  }

  if ('termsSnapshot' in promotion && promotion.termsSnapshot) {
    return promotion.termsSnapshot
  }

  return null
}
</script>
